import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import classes from '../templates.module.scss';
import formClasses from '../../styles.module.scss';
import { createCn } from '../../../helpers/utils';
import { ProtocolTemplateSectionFormat } from '../../../services/templates.service';

const cn = createCn('templates', classes);

/** key задан только у секций, загруженных с сервера — у новых строк он вычисляется при сохранении. */
export type SectionRow = {
  id: string;
  key?: string;
  title: string;
  hint: string;
  /** Задача TPL1: детальная инструкция для LLM — расширяет старое поле hint. */
  instruction: string;
  /** Задача TPL1: ожидаемый формат содержимого секции, по умолчанию "paragraph". */
  format: ProtocolTemplateSectionFormat;
  /** Задача TPL1: формат одного элемента списка — имеет смысл только при format === 'list'. */
  itemFormat: string;
};

const SECTION_FORMATS: ProtocolTemplateSectionFormat[] = ['paragraph', 'list', 'string'];

let rowSeq = 0;

export function createEmptySectionRow(): SectionRow {
  rowSeq += 1;
  return { id: `new-${rowSeq}`, title: '', hint: '', instruction: '', format: 'paragraph', itemFormat: '' };
}

interface SectionsEditorProps {
  rows: SectionRow[];
  onChange: (rows: SectionRow[]) => void;
  disabled?: boolean;
}

const SectionsEditor = ({ rows, onChange, disabled }: SectionsEditorProps) => {
  const { t } = useTranslation();

  const updateRow = (id: string, patch: Partial<SectionRow>) => {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string) => {
    onChange(rows.filter((row) => row.id !== id));
  };

  const addRow = () => {
    onChange([...rows, createEmptySectionRow()]);
  };

  return (
    <div className={cn('sections-editor')}>
      <div className={cn('sections-editor-label')}>Секции протокола</div>

      {rows.map((row, index) => (
        <div key={row.id} className={cn('section-row')}>
          <div className={cn('section-row-index')}>{index + 1}</div>

          <div className={cn('section-row-fields')}>
            <div className={formClasses['form-group']}>
              <label>Заголовок секции</label>
              <input
                value={row.title}
                onChange={(e) => updateRow(row.id, { title: e.target.value })}
                placeholder="например, Повестка дня"
                disabled={disabled}
              />
            </div>

            <div className={formClasses['form-group']}>
              <label>Подсказка для AI (необязательно)</label>
              <textarea
                value={row.hint}
                onChange={(e) => updateRow(row.id, { hint: e.target.value })}
                placeholder="Что должна содержать эта секция"
                disabled={disabled}
              />
            </div>

            <div className={formClasses['form-group']}>
              <label>{t('templates.section-instruction-label')}</label>
              <textarea
                value={row.instruction}
                onChange={(e) => updateRow(row.id, { instruction: e.target.value })}
                placeholder={t('templates.section-instruction-placeholder') as string}
                disabled={disabled}
              />
            </div>

            <div className={cn('section-row-format-group')}>
              <div className={formClasses['form-group']}>
                <label>{t('templates.section-format-label')}</label>
                <select
                  value={row.format}
                  onChange={(e) => updateRow(row.id, { format: e.target.value as ProtocolTemplateSectionFormat })}
                  disabled={disabled}
                >
                  {SECTION_FORMATS.map((format) => (
                    <option key={format} value={format}>
                      {t(`templates.section-format-option-${format}`)}
                    </option>
                  ))}
                </select>
              </div>

              {row.format === 'list' && (
                <div className={formClasses['form-group']}>
                  <label>{t('templates.section-item-format-label')}</label>
                  <input
                    value={row.itemFormat}
                    onChange={(e) => updateRow(row.id, { itemFormat: e.target.value })}
                    placeholder={t('templates.section-item-format-placeholder') as string}
                    disabled={disabled}
                  />
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            className={cn('section-row-remove')}
            onClick={() => removeRow(row.id)}
            disabled={disabled || rows.length <= 1}
            title={rows.length <= 1 ? 'Должна остаться хотя бы одна секция' : 'Удалить секцию'}
            aria-label="Удалить секцию"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}

      <button type="button" className={cn('add-section-btn')} onClick={addRow} disabled={disabled}>
        <Plus size={16} /> Добавить секцию
      </button>
    </div>
  );
};

export default SectionsEditor;
